import React, { useState, useEffect } from 'react';
import { AlertCircle, Clock, CheckCircle } from 'lucide-react';
import { getAIServiceRateLimitStatus } from '../../services/aiService';

/**
 * Rate Limit Indicator Component
 * Displays current API usage and rate limit status
 */
export default function RateLimitIndicator({ className = '' }) {
  const [status, setStatus] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Update status every 5 seconds
    const updateStatus = () => {
      const currentStatus = getAIServiceRateLimitStatus();
      setStatus(currentStatus);
      
      // Show indicator if usage is > 50% or rate limited
      const showThreshold = 0.5;
      const minuteUsageRatio = (currentStatus?.usage?.minuteRequests || 0) / (currentStatus?.limits?.requestsPerMinute || 1);
      const shouldShow = minuteUsageRatio > showThreshold || currentStatus?.isLimited;
      
      setIsVisible(shouldShow);
    };

    updateStatus();
    const interval = setInterval(updateStatus, 5000);

    return () => clearInterval(interval);
  }, []);

  if (!isVisible || !status) return null;

  const getStatusColor = () => {
    if (status?.isLimited) return 'bg-red-100 border-red-400 text-red-800';
    
    const minuteRatio = (status?.usage?.minuteRequests || 0) / (status?.limits?.requestsPerMinute || 1);
    if (minuteRatio > 0.8) return 'bg-yellow-100 border-yellow-400 text-yellow-800';
    return 'bg-blue-100 border-blue-400 text-blue-800';
  };

  const getStatusIcon = () => {
    if (status?.isLimited) return <AlertCircle className="w-5 h-5" />;
    
    const minuteRatio = (status?.usage?.minuteRequests || 0) / (status?.limits?.requestsPerMinute || 1);
    if (minuteRatio > 0.8) return <Clock className="w-5 h-5" />;
    return <CheckCircle className="w-5 h-5" />;
  };

  const getStatusMessage = () => {
    if (status?.isLimited) {
      const resetTime = status?.resetTime ? new Date(status?.resetTime) : null;
      const waitSeconds = resetTime ? Math.ceil((resetTime - Date.now()) / 1000) : 0;
      return `Rate limit reached. Resets in ${waitSeconds}s`;
    }

    return `API Usage: ${status?.usage?.minuteRequests || 0}/${status?.limits?.requestsPerMinute || 0} requests/min`;
  };

  const calculatePercentage = (used, limit) => {
    return Math.min(100, Math.round((used / limit) * 100));
  };

  return (
    <div className={`${getStatusColor()} border rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {getStatusIcon()}
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold mb-2">
            {getStatusMessage()}
          </p>
          
          <div className="space-y-2">
            {/* Requests per minute */}
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span>Requests/Min</span>
                <span>
                  {status?.remaining?.minuteRequests || 0} remaining
                </span>
              </div>
              <div className="w-full bg-white bg-opacity-50 rounded-full h-2">
                <div
                  className="bg-current rounded-full h-2 transition-all duration-300"
                  style={{
                    width: `${calculatePercentage(
                      status?.usage?.minuteRequests || 0,
                      status?.limits?.requestsPerMinute || 1
                    )}%`,
                  }}
                />
              </div>
            </div>

            {/* Tokens per minute */}
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span>Tokens/Min</span>
                <span>
                  {(status?.remaining?.minuteTokens || 0)?.toLocaleString()} remaining
                </span>
              </div>
              <div className="w-full bg-white bg-opacity-50 rounded-full h-2">
                <div
                  className="bg-current rounded-full h-2 transition-all duration-300"
                  style={{
                    width: `${calculatePercentage(
                      status?.usage?.minuteTokens || 0,
                      status?.limits?.tokensPerMinute || 1
                    )}%`,
                  }}
                />
              </div>
            </div>
          </div>

          {status?.isLimited && (
            <p className="text-xs mt-2">
              Requests are automatically queued and will resume when limits reset.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}